import classNames from "classnames";
import { SearchInput } from "components/Atoms/SearchInput";
import CultureFlag from "components/Culture/CultureFlag";
import { Loading } from "components/panels";
import React from "react";
import { getCityStateCountry } from "shared/utils/addressUtils";
import { NoDataAvailableDiv } from "../NoDataAvailableDiv";
import { useAddressbookSearch } from "./hooks";

interface Props {
  suggestedApiInstruction: server.dto.ListContactAddressBookToItemInstruction
  onContactClick: (entry: server.dto.ContactAddressBookEntry, email: string, company?: server.dto.CompanySmall) => void
}

interface HoveringOverEntry {
  entry: server.dto.ContactAddressBookEntry
  company: server.dto.CompanySmall
  email: string
}

const CompanyAndEmailAddressSearch = (props: React.PropsWithChildren<Props>) => {
  const addressBookSearch = useAddressbookSearch({ suggestedApiInstruction: props.suggestedApiInstruction });
  const [hoveringOver, setHoveringOver] = React.useState<HoveringOverEntry>(undefined);

  const getEmailClasses = (entry: HoveringOverEntry) => {
    if (!hoveringOver) {
      return "padding";
    }

    return classNames("flex align-center padding",
      {
        highlighted: hoveringOver.entry.contactId === entry.entry.contactId && hoveringOver.email === entry.email && hoveringOver.company?.companyId === entry.company?.companyId
      }
    );
  };

  const onClick = () => {
    props.onContactClick(hoveringOver.entry, hoveringOver.email ?? "", hoveringOver.company);
  };

  return <>
    <SearchInput
      focusOnMount
      value={addressBookSearch.searchInput}
      placeholder={RESX.Contact.KeywordPlaceholder}
      onChange={value => { addressBookSearch.setSearchInput(value); }}
    />

    <div className="margin-top margin-bottom scrollbox-auto grow1">
      {!addressBookSearch.isLoading && addressBookSearch.results.length > 0 && <>
        {addressBookSearch.results.map((item, idx) =>
          <div
            ref={addressBookSearch.shouldIncludeVisibilityRef(idx) ? addressBookSearch.ref : undefined}
            style={{ cursor: "pointer" }}
            className="padding margin-bottom-small panel-bg-color"
            key={item.contactId}
            onClick={onClick}
            onMouseEnter={() => { setHoveringOver({ entry: item, company: item.companies[0], email: item.emails[0] ?? "" }); }}
            onMouseLeave={() => { setHoveringOver(undefined); }}
          >
            <div className="flex padding">
              <div className="grow1">
                <div className="margin-bottom-mini"><h3>{item.name.fullName}</h3></div>
                <div className="text-sublabel">{item.position}</div>
              </div>

              <div>
                <CultureFlag lcid={item.culture} size="medium" />
              </div>
            </div>
            <div className="flex-column" style={{ gap: "5px 0px" }}>
              {/* Some contacts have emails but no company  */}
              {item.companies.length === 0 && <React.Fragment>
                <div className="margin-small" style={{ borderBottom: "1px solid #BBB" }} />
                <div
                  className="flex"
                  onMouseEnter={() => { setHoveringOver({ entry: item, company: undefined, email: item.emails[0] ?? "" }); }}
                  onMouseLeave={() => { setHoveringOver({ entry: item, company: undefined, email: item.emails[0] ?? "" }); }}
                >
                  <div style={{ flex: 1 }} className="flex-column">
                    {item.emails.map((email) =>
                      <div
                        style={{ display: "inline-block", textDecoration: "underline" }}
                        key={email}
                        className={getEmailClasses({ entry: item, company: null, email })}
                        onMouseEnter={() => { setHoveringOver({ entry: item, company: null, email }); }}
                        onMouseLeave={() => { setHoveringOver({ entry: item, company: null, email: item.emails[0] ?? "" }); }}
                      >
                        {email}
                      </div>
                    )}
                  </div>
                  <div style={{ flex: 1.5 }} >

                  </div>
                </div>
              </React.Fragment>}
              {item.companies.map((company) =>
                <React.Fragment key={company?.companyId}>
                  <div className="margin-small" style={{ borderBottom: "1px solid #BBB" }} />
                  <div
                    className="flex"
                    onMouseEnter={() => { setHoveringOver({ entry: item, company, email: item.emails[0] ?? "" }); }}
                    onMouseLeave={() => { setHoveringOver({ entry: item, company: item.companies[0], email: item.emails[0] ?? "" }); }}
                  >
                    <div style={{ flex: 1 }} className="flex-column">
                      {item.emails.map((email) =>
                        <div
                          style={{ display: "inline-block", textDecoration: "underline" }}
                          key={email}
                          className={getEmailClasses({ entry: item, company, email })}
                          onMouseEnter={() => { setHoveringOver({ entry: item, company, email }); }}
                          onMouseLeave={() => { setHoveringOver({ entry: item, company, email: item.emails[0] ?? "" }); }}
                        >
                          {email}
                        </div>
                      )}
                    </div>
                    <div style={{ flex: 1.5 }} >
                      <div className="padding flex">
                        <div>
                          <div>{company.name}</div>
                          <div className="text-sublabel">{getCityStateCountry(company.address)}</div>
                        </div>
                      </div>
                    </div>

                  </div>
                </React.Fragment>
              )}
            </div>
          </div>)}
      </>}
      {!addressBookSearch.isLoading && addressBookSearch.results.length === 0 && <div>
        <NoDataAvailableDiv />
      </div>}

      {addressBookSearch.isLoading && <Loading />}
    </div >

    {props.children}
  </>;
};

export default CompanyAndEmailAddressSearch;